import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import Logo from "@/components/Logo";
import Splash from "@/components/Splash";
import Field from "@/components/admin/Field";
import CollectionsEditor from "@/components/admin/CollectionsEditor";
import MessagesTable from "@/components/admin/MessagesTable";
import MediaLibrary from "@/components/admin/MediaLibrary";
import TeamTab from "@/components/admin/TeamTab";
import ForcePasswordChange from "@/components/admin/ForcePasswordChange";
import { recordContext } from "@/lib/aiStudio";

const THEMES = [
  { id: "sovereign-prestige", name: "Sovereign Prestige", mood: "Royal navy & gold — institutional investment luxury", colors: ["#070B12", "#D4AF37", "#F8FAFC"] },
  { id: "desert-minimalist", name: "Desert Minimalist", mood: "Sand & silk — warm, architectural, editorial light", colors: ["#FDFBF7", "#C28E0E", "#1C1917"] },
  { id: "industrial-titan", name: "Industrial Titan", mood: "Steel & amber — bold industrial engineering", colors: ["#0B0F17", "#F59E0B", "#F1F5F9"] },
  { id: "oasis-emerald", name: "Oasis Emerald", mood: "Emerald & ivory — sovereign wealth and growth", colors: ["#041C15", "#EAB308", "#F0FDFA"] },
];

const SETTINGS_GROUPS = [
  {
    title: "Identity & Logo",
    fields: [
      { key: "site_name", label: "Site Name (EN / AR)", type: "loc" },
      { key: "site_name_alt", label: "Full Legal Name (EN / AR)", type: "loc" },
      { key: "logo_url", label: "Logo — dark backgrounds (upload your file or paste a URL)", type: "image" },
      { key: "logo_url_light", label: "Logo — light backgrounds / Desert Minimalist template", type: "image" },
      { key: "logo_height_nav", label: "Logo height in the navigation bar (px)", type: "number", min: 20, max: 160 },
      { key: "logo_height_footer", label: "Logo height in the footer (px)", type: "number", min: 20, max: 320 },
    ],
  },
  {
    title: "Hero Stage — photos, videos & YouTube",
    fields: [{ key: "hero_slides", label: "Hero slides", type: "slidelist" }],
  },
  {
    title: "Default Hero Headline (used by slides with blank text)",
    fields: [
      { key: "hero_eyebrow", label: "Eyebrow Line (EN / AR)", type: "loc" },
      { key: "hero_lines", label: "Headline Lines", type: "loclist" },
      { key: "hero_tagline", label: "Tagline", type: "locarea" },
      { key: "video_url", label: "Fallback hero video (used when no slides exist)", type: "media", accept: "video" },
      { key: "poster_url", label: "Fallback poster image", type: "media", accept: "image" },
    ],
  },
  {
    title: "Story & Chairman",
    fields: [
      { key: "about_intro", label: "About Intro", type: "locarea" },
      { key: "mission", label: "Mission", type: "locarea" },
      { key: "vision", label: "Vision", type: "locarea" },
      { key: "chairman_name", label: "Chairman Name", type: "text" },
      { key: "chairman_title", label: "Chairman Title", type: "loc" },
      { key: "chairman_quote", label: "Chairman Quote", type: "locarea" },
    ],
  },
  { title: "Key Figures (hero stats bar)", fields: [{ key: "stats", label: "Stats", type: "statlist" }] },
  {
    title: "Strategic Partner",
    fields: [{ key: "spotlight", label: "Strategic partner", type: "spotlight" }],
  },
  {
    title: "Contact Details",
    fields: [
      { key: "address", label: "HQ Address", type: "textarea" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "linkedin", label: "LinkedIn URL", type: "text" },
      { key: "response_time", label: "Response-time note (shown beside the contact form)", type: "locarea" },
    ],
  },
  { title: "Governance Principles (Leadership page)", fields: [{ key: "governance_pillars", label: "Principles", type: "pillarlist" }] },
  {
    title: "Search & Social (SEO)",
    fields: [
      { key: "seo_title_suffix", label: "Brand name appended to every page title", type: "loc" },
      { key: "seo_description", label: "Default meta description — used when a page has none", type: "locarea" },
      { key: "seo_image", label: "Default link preview image (1200 × 630 works best)", type: "media", accept: "image" },
      { key: "seo_keywords", label: "Keywords (comma separated)", type: "textarea" },
      {
        key: "canonical_url",
        label: "Canonical website address — e.g. https://alqalaainvestment.com. Visitors on the www spelling are sent here, and every link preview and sitemap entry uses it.",
        type: "text",
      },
      { key: "page_seo", label: "Per-page titles and descriptions", type: "seolist" },
    ],
  },
  {
    title: "Closing CTA & Marquee",
    fields: [
      { key: "cta_title", label: "CTA Title", type: "loc" },
      { key: "cta_body", label: "CTA Body", type: "locarea" },
      { key: "marquee_items", label: "Marquee Items", type: "loclist" },
    ],
  },
];

function ThemeTab() {
  const site = useSite();
  const current = site?.settings?.theme;
  const apply = async (id) => {
    try {
      await api.put("/admin/settings", { theme: id });
      await site.refresh();
      toast.success(`Template applied: ${id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <p className="mb-6 text-sm text-muted-foreground">Pick the template the whole website uses. The change is live the moment you click.</p>
      <div className="grid gap-5 md:grid-cols-2">
        {THEMES.map((th) => (
          <button
            key={th.id}
            type="button"
            data-testid={`theme-select-${th.id}`}
            onClick={() => apply(th.id)}
            className={`card-lift border p-6 text-start ${current === th.id ? "border-primary" : "border-border"}`}
            style={{ borderRadius: "var(--radius)" }}
          >
            <div className="mb-4 flex gap-2">
              {th.colors.map((c) => (
                <span key={c} className="h-8 w-8 rounded-full border border-border" style={{ background: c }} />
              ))}
            </div>
            <p className="font-head text-2xl">{th.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{th.mood}</p>
            {current === th.id && <span className="eyebrow mt-4 inline-block">● Active template</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const site = useSite();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (site?.settings && form === null) setForm(JSON.parse(JSON.stringify(site.settings)));
  }, [site?.settings, form]);

  if (!form) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      await site.refresh();
      toast.success("Website content updated — live now");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8">
      {SETTINGS_GROUPS.map((g) => (
        <div key={g.title} className="border border-border p-6 md:p-8" style={{ borderRadius: "var(--radius)" }}>
          <h3 className="mb-6 font-head text-2xl">{g.title}</h3>
          <div className="grid gap-5 md:grid-cols-2">
            {g.fields.map((f) => (
              <Field
                key={f.key}
                field={f}
                value={form[f.key]}
                context={recordContext(form, g.fields, f.key)}
                onChange={setF(f.key)}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} data-testid="settings-save-btn" className="btn-gold shadow-xl">
          Save All Changes
        </button>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border border-border bg-card/40 p-9 md:p-11" style={{ borderRadius: "var(--radius)" }}>
        <div className="mb-8 flex items-center gap-3 text-primary">
          <Logo markSize={40} siteName="Al Qalaa" />
          <div>
            <p className="font-head text-2xl">Content Studio</p>
            <p className="text-xs text-muted-foreground">Al Qalaa International Group</p>
          </div>
        </div>
        <div className="grid gap-5">
          <div className="grid gap-2">
            <label htmlFor="admin-email" className="text-xs text-muted-foreground">{t("admin_email")}</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="admin-password" className="text-xs text-muted-foreground">{t("admin_password")}</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <button type="submit" data-testid="admin-login-button" disabled={busy} className="btn-gold w-full disabled:opacity-60">
            {busy ? "Signing in..." : t("admin_sign_in")}
          </button>
        </div>
      </form>
    </div>
  );
}

const TABS = [
  { id: "template", label: "Template" },
  { id: "content", label: "Website Content" },
  { id: "sectors", label: "Sectors" },
  { id: "ventures", label: "Joint Ventures" },
  { id: "leaders", label: "Leadership" },
  { id: "offices", label: "Offices" },
  { id: "locations", label: "Map Pins" },
  { id: "partners", label: "Partners" },
  { id: "posts", label: "News & Insights" },
  { id: "media", label: "Media" },
  { id: "messages", label: "Messages" },
  { id: "team", label: "Team" },
];

const SECTOR_FIELDS = [
  { key: "name", label: "Sector Name (EN / AR)", type: "loc" },
  { key: "description", label: "Description", type: "locarea" },
  { key: "image", label: "Hero image for this sector", type: "media", accept: "image" },
  { key: "capabilities", label: "Capabilities", type: "loclist" },
  { key: "metrics", label: "At-a-glance metrics (shown on the Sectors page)", type: "statlist" },
  { key: "related_ventures", label: "Related joint venture IDs (e.g. qtrasteel, q-perifa)", type: "strlist", placeholder: "qtrasteel" },
  { key: "gallery", label: "Project gallery (upload or paste image URLs, with captions)", type: "gallerylist" },
];
const VENTURE_FIELDS = [
  { key: "name", label: "Venture Name", type: "text" },
  { key: "date", label: "Launch Date (e.g. May 2026)", type: "text" },
  { key: "partner", label: "Partner", type: "text" },
  { key: "partner_origin", label: "Partner Origin", type: "text" },
  { key: "partner_code", label: "Partner Country Code for flag (e.g. ch, au, de)", type: "text" },
  { key: "region", label: "Region", type: "text" },
  { key: "badge", label: "Badge", type: "loc" },
  { key: "focus", label: "Focus", type: "locarea" },
  { key: "description", label: "Description", type: "locarea" },
  { key: "activities", label: "Key Activities", type: "loclist" },
  { key: "partner_network", label: "Manufacturing & partner network (shown as badges)", type: "strlist", placeholder: "Roche" },
  { key: "image", label: "Venture image", type: "media", accept: "image" },
];
const LEADER_FIELDS = [
  { key: "name", label: "Full Name", type: "text" },
  { key: "title", label: "Title (EN / AR)", type: "loc" },
  { key: "bio", label: "Biography", type: "locarea" },
  { key: "photo_url", label: "Photo — upload or paste a URL", type: "image" },
  { key: "linkedin", label: "LinkedIn Profile URL", type: "text" },
  { key: "group", label: "Group", type: "select", options: [{ value: "board", label: "Executive Board" }, { value: "advisory", label: "Advisory Council" }] },
];
const POST_FIELDS = [
  { key: "title", label: "Headline (EN / AR)", type: "loc" },
  { key: "slug", label: "URL slug (e.g. gypto-joint-venture-signed)", type: "text" },
  { key: "category", label: "Category", type: "select", options: [{ value: "announcement", label: "Announcement" }, { value: "jv-milestone", label: "JV Milestone" }, { value: "sector-insight", label: "Sector Insight" }] },
  { key: "date", label: "Publish date (YYYY-MM-DD)", type: "text" },
  { key: "excerpt", label: "Standfirst / summary", type: "locarea" },
  { key: "body", label: "Article body — separate paragraphs with a blank line", type: "locarea" },
  { key: "image", label: "Cover image — upload or paste a URL", type: "image" },
  { key: "featured", label: "Feature this article at the top of the press room", type: "switch" },
  { key: "seo_title", label: "SEO title override (optional — defaults to the headline)", type: "loc" },
  { key: "seo_description", label: "Link preview description (optional — defaults to the summary)", type: "locarea" },
];
const OFFICE_FIELDS = [
  { key: "country", label: "Country (EN / AR)", type: "loc" },
  { key: "city", label: "City (EN / AR)", type: "loc" },
  { key: "kind", label: "Office Type (EN / AR) — e.g. Headquarters, Regional Office, Branch Office", type: "loc" },
  { key: "address", label: "Address (EN / AR)", type: "locarea" },
  { key: "phone", label: "Direct phone (optional)", type: "text" },
  { key: "map_query", label: "Map search text (used for the Contact page map)", type: "text" },
  { key: "is_hq", label: "This is the Headquarters", type: "switch" },
];
const PARTNER_FIELDS = [
  { key: "name", label: "Partner name (as it should read under the logo)", type: "text" },
  { key: "logo", label: "Logo — upload a file or search the web below", type: "logo" },
  { key: "link", label: "Their website (optional, opens in a new tab)", type: "text" },
  { key: "note", label: "What they are to us (EN / AR) — shown on hover", type: "loc" },
];
const LOCATION_FIELDS = [
  { key: "name", label: "Pin Name (EN / AR)", type: "loc" },
  {
    key: "kind", label: "What is it?", type: "select",
    options: [
      { value: "office", label: "Office" },
      { value: "plant", label: "Plant / Factory" },
      { value: "project", label: "Project / Development" },
      { value: "venture", label: "Joint Venture" },
    ],
  },
  {
    key: "country", label: "Country", type: "select",
    options: [{ value: "uae", label: "United Arab Emirates" }, { value: "egypt", label: "Egypt" }],
  },
  { key: "city", label: "City / Area (EN / AR)", type: "loc" },
  { key: "address", label: "Address (EN / AR)", type: "locarea" },
  { key: "summary", label: "One-line description shown in the pin popup", type: "locarea" },
  { key: "lat", label: "Latitude (e.g. 24.4304)", type: "number", min: -90, max: 90 },
  { key: "lng", label: "Longitude (e.g. 54.4652)", type: "number", min: -180, max: 180 },
  { key: "link", label: "Link when the popup is clicked (e.g. /joint-ventures)", type: "text" },
];

export default function Admin() {
  const { t } = useLang();
  const site = useSite();
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("template");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined) return <Splash />;
  if (!user) return <LoginScreen onLogin={setUser} />;
  if (user.must_change_password) return <ForcePasswordChange user={user} onDone={setUser} />;
  if (user.role !== "admin") return <Navigate to="/studio" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="glass-nav sticky top-0 z-40 border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-3 text-primary">
            {site?.settings?.logo_url ? (
              <img src={site.settings.logo_url} alt="Al Qalaa" className="h-11 w-auto" />
            ) : (
              <Logo markSize={30} siteName="Al Qalaa" />
            )}
            <div className="leading-tight">
              <p className="font-head text-lg">Content Studio</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="btn-outline h-9 px-4 text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> {t("admin_view_site")}
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="btn-outline h-9 px-4 text-xs">
              <LogOut className="h-3.5 w-3.5" /> {t("admin_logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-8 flex flex-wrap gap-2 border-b border-border pb-4">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              type="button"
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`h-9 rounded-full px-4 text-xs font-semibold tracking-wide transition-colors ${tab === tb.id ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:text-primary"}`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "template" && <ThemeTab />}
        {tab === "content" && <SettingsTab />}
        {tab === "sectors" && (
          <CollectionsEditor
            collection="sectors"
            title="Sectors"
            fields={SECTOR_FIELDS}
            display={(it) => it.name?.en || "Untitled sector"}
            newItem={{ name: { en: "", ar: "" }, description: { en: "", ar: "" }, image: "", capabilities: [], metrics: [], related_ventures: [], gallery: [], order: 99 }}
          />
        )}
        {tab === "ventures" && (
          <CollectionsEditor
            collection="ventures"
            title="Joint Ventures"
            fields={VENTURE_FIELDS}
            display={(it) => it.name || "Untitled venture"}
            newItem={{ name: "", date: "", partner: "", partner_origin: "", partner_code: "", region: "", badge: { en: "", ar: "" }, focus: { en: "", ar: "" }, description: { en: "", ar: "" }, activities: [], partner_network: [], image: "", order: 99 }}
          />
        )}
        {tab === "leaders" && (
          <CollectionsEditor
            collection="leaders"
            title="Leadership"
            fields={LEADER_FIELDS}
            display={(it) => it.name || "Untitled person"}
            newItem={{ name: "", title: { en: "", ar: "" }, bio: { en: "", ar: "" }, photo_url: "", linkedin: "", group: "board", order: 99 }}
          />
        )}
        {tab === "offices" && (
          <CollectionsEditor
            collection="offices"
            title="Offices"
            fields={OFFICE_FIELDS}
            display={(it) => it.country?.en || "Untitled office"}
            newItem={{ country: { en: "", ar: "" }, city: { en: "", ar: "" }, address: { en: "", ar: "" }, kind: { en: "Regional Office", ar: "مكتب إقليمي" }, map_query: "", is_hq: false, order: 99 }}
          />
        )}
        {tab === "locations" && (
          <CollectionsEditor
            collection="locations"
            title="Map Pins — UAE & Egypt"
            fields={LOCATION_FIELDS}
            display={(it) => it.name?.en || "Untitled pin"}
            newItem={{
              name: { en: "", ar: "" }, kind: "project", country: "uae",
              city: { en: "", ar: "" }, address: { en: "", ar: "" }, summary: { en: "", ar: "" },
              lat: 24.4539, lng: 54.3774, link: "", order: 99,
            }}
          />
        )}
        {tab === "partners" && (
          <CollectionsEditor
            collection="partners"
            title="Partner Logo Wall"
            fields={PARTNER_FIELDS}
            display={(it) => it.name || "Untitled partner"}
            newItem={{ name: "", logo: "", link: "", note: { en: "", ar: "" }, order: 99 }}
          />
        )}
        {tab === "posts" && (
          <CollectionsEditor
            collection="posts"
            title="News & Insights"
            fields={POST_FIELDS}
            display={(it) => it.title?.en || "Untitled article"}
            newItem={{ title: { en: "", ar: "" }, slug: "", category: "announcement", date: "", excerpt: { en: "", ar: "" }, body: { en: "", ar: "" }, image: "", featured: false, seo_title: { en: "", ar: "" }, seo_description: { en: "", ar: "" }, order: 1 }}
          />
        )}
        {tab === "media" && <MediaLibrary />}
        {tab === "team" && <TeamTab />}
        {tab === "messages" && <MessagesTable />}
      </div>
    </div>
  );
}
